import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {DateTime} from "luxon";
import {
    fetchOdPLegacy,
    OdPLegacy,
    OdPLegacyList,
    STATI_ODP,
} from "../produzione/odpLegacySlice";
import {
    deleteFaseFrazione, deleteFrazioneOdP,
    FaseFrazione,
    fetchFrazioniOdP, FrazioneOdP, rischedulaFaseFrazione, rischedulaFrazione, schedulaFrazione,
} from "./frazioniOdPSlice";
import {
    fetchReparti,
    Reparto,
} from "../../anagrafiche/reparti/repartiSlice";
import {
    fetchOperatori,
    Operatore,
} from "../personale/operatoriSlice";

import {
    FiltriOdPLegacy, FiltriVisualizzazioneGantt,
    setFiltriOdPSpallaSchedulazione, setFiltriVisualizzazioneGantt,
} from "components/userPreference/userPreferenceSlice";
import {
    fetchCaricoReparti,
    fetchCaricoReparto,
    resetCaricoDettaglio,
} from "./caricoRepartiSlice";
import {
    fetchVariazioni,
    saveVariazione,
    updateVariazionePartial,
    VariazioneDisponibilita,
} from "./variazioniDisponibilitaSlice";
import { toast } from "react-toastify";
import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import OdPSidebarCard from "./OdPSidebarCard";
import SchedulazioneGanttTimeline from "./gantt/SchedulazioneGanttTimeline";
import FaseFrazioneEditModal from "./FaseFrazioneEditModal";
import VariazioneEditModal from "./VariazioneEditModal";
import VariazioneSidebarCard from "./VariazioneSidebarCard";
import {useFiltriOdPLegacy} from "./useFiltriOdPLegacy";
import {OPZIONI_PERIODO, SENZA_OPERATORE_ID} from "./gantt/GanttHeader";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SearchIcon from "@mui/icons-material/Search";
import FactoryIcon from "@mui/icons-material/Factory";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import TuneIcon from "@mui/icons-material/Tune";
import TodayIcon from "@mui/icons-material/Today";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {theme} from "../../theme";
import ConfermaAnnulla from "../../common/ConfermaAnnulla";
import {convertToDateTime} from "../../common/dateUtils/convertToDateTime";
import {AutocompleteClientiAsync} from "../../anagrafiche/clienti/autocomplete/AutocompleteClientiAsync";

// ============================================
// COMPONENT
// ============================================

const SchedulazionePage = () => {
    const dispatch = useAppDispatch();

    // ============================================
    // SELECTORS
    // ============================================

    const odpLegacyList = useSelector<RootState, OdPLegacyList>(
        (state) => state.odpLegacyState.odpLegacyList
    );

    const frazioniGantt = useSelector<RootState, FrazioneOdP[]>(
        (state) => state.frazioniOdPState?.frazioniOdPList?.results || []
    );

    const filtriOdP = useSelector<RootState, FiltriOdPLegacy>(
        (state) => state.userPreferenceState.filtriOdPSpallaSchedulazione || {}
    );

    const filtriVisualizzazioneGantt = useSelector<RootState, FiltriVisualizzazioneGantt>(
        (state) => state.userPreferenceState.filtriVisualizzazioneGantt || {dataDa: convertToDateTime(new Date())?.setLocale("it").toFormat("yyyy-MM-dd"), numeroSettimane: 4},
    );

    const reparti = useSelector<RootState, Reparto[]>(
        (state) => state.repartiState?.repartiList?.results || []
    );

    const operatori = useSelector<RootState, Operatore[]>(
        (state) => state.operatoriState?.operatoriList?.results || []
    );

    // ============================================
    // STATI LOCALI
    // ============================================

    const [reset, setReset] = useState(true);
    const [refreshOdp, setRefreshOdp] = useState(false);
    const [sidebarAperta, setSidebarAperta] = useState(true);
    const [filtriVisibili, setFiltriVisibili] = useState(false);

    // Gantt state
    const [ganttDataDa, setGanttDataDa] = useState<string>(filtriVisualizzazioneGantt?.dataDa || "");
    const [numeroSettimane, setNumeroSettimane] = useState<2 | 4 | 6>(filtriVisualizzazioneGantt.numeroSettimane);

    // Filtri OdP Legacy: stati locali + dispatch automatico al cambio
    const filtri = useFiltriOdPLegacy(operatori);

    // OdP attivo: evidenzia contemporaneamente sidebar e frazioni nel gantt
    const [odpAttivoId, setOdpAttivoId] = useState<number | undefined>(undefined);

    const [openConferma, setOpenConferma] = useState(false);
    const [frazioneToDelete, setFrazioneToDelete] = useState<FrazioneOdP | undefined>(undefined);
    const [faseFrazioneToDelete, setFaseFrazioneToDelete] = useState<FaseFrazione | undefined>(undefined);
    const [faseFrazioneToEdit, setFaseFrazioneToEdit] = useState<FaseFrazione | undefined>(undefined);
    const [openEditFaseFrazione, setOpenEditFaseFrazione] = useState(false);

    const [variazioneToEdit, setVariazioneToEdit] = useState<VariazioneDisponibilita | undefined>(undefined);
    const [openVariazioneModal, setOpenVariazioneModal] = useState(false);
    const handleConfermaClose = () => {
        setFrazioneToDelete(undefined)
        setFaseFrazioneToDelete(undefined)
        setOpenConferma(false)
    };
    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        dispatch(fetchReparti());
        dispatch(fetchOperatori());
    }, []);

    useEffect(() => {
        const filtriInit = {
            ...filtriOdP,
            numero_record: filtriOdP.numero_record || 10,
            page: filtriOdP.page || 1,
        };
        if (!filtriOdP.numero_record) {
            dispatch(setFiltriOdPSpallaSchedulazione(filtriInit));
        }
        dispatch(fetchOdPLegacy(filtriInit));
        setRefreshOdp(false)
    }, [refreshOdp]);

    // Fetch frazioni quando cambia il range del gantt
    useEffect(() => {
        const dataDaISO = convertToDateTime(ganttDataDa)?.startOf("week").toFormat("yyyy-MM-dd");
        if (!dataDaISO) return;
        const dataA = convertToDateTime(dataDaISO)?.plus({weeks: numeroSettimane}).toFormat("yyyy-MM-dd");
        dispatch(
            fetchFrazioniOdP({
                data_da: dataDaISO,
                data_a: dataA,
                // Il Gantt è una vista "a finestra": deve mostrare TUTTE le frazioni
                // del range, non la sola pagina 1 (default BE 20). Allargando la
                // finestra a 4/6 settimane le frazioni oltre la 20ª finivano in
                // pagina 2+ (mai richiesta) e sparivano dal Gantt.
                numero_record: 1000,
            })
        );
        setReset(false)
    }, [ganttDataDa, numeroSettimane, reset]);

    // Fetch carico reparti quando cambia il range del gantt
    useEffect(() => {
        if (!ganttDataDa) return;
        const dataDaISO = convertToDateTime(ganttDataDa)?.startOf("week").toFormat("yyyy-MM-dd");
        if (!dataDaISO) return;
        const giorni = numeroSettimane * 7;
        dispatch(fetchCaricoReparti({ dataDa: dataDaISO, giorni }));
    }, [ganttDataDa, numeroSettimane]);

    // Fetch variazioni di disponibilità quando cambia il range del gantt o i filtri reparti/operatori
    useEffect(() => {
        if (!ganttDataDa) return;
        const dataDaISO = convertToDateTime(ganttDataDa)?.startOf("week").toFormat("yyyy-MM-dd");
        if (!dataDaISO) return;
        const dataA = convertToDateTime(dataDaISO)?.plus({ weeks: numeroSettimane }).toFormat("yyyy-MM-dd");
        if (!dataA) return;
        dispatch(fetchVariazioni({
            dataDa: dataDaISO,
            dataA,
            repartiIds: filtri.repartiSelezionati.length > 0 ? filtri.repartiSelezionati : undefined,
            includeOperatoriDiReparto: filtri.repartiSelezionati.length > 0,
        }));
    }, [ganttDataDa, numeroSettimane, filtri.repartiSelezionati]);

    // Quando l'OdP attivo viene selezionato dal Gantt e non è nella pagina
    // corrente della spalla, chiede al BE di restituire la pagina che lo
    // contiene (parametro goto_id) e allinea la paginazione lato preferenze.
    useEffect(() => {
        if (!odpAttivoId) return;
        if (odpLegacyList.results.some((o) => o.id === odpAttivoId)) return;
        if (odpLegacyList.errorsStack.status === ErrorStatusTypes.PENDING) return;

        const filtriPerGoto = {...filtriOdP, goto_id: odpAttivoId};
        dispatch(fetchOdPLegacy(filtriPerGoto)).then((res) => {
            if (
                res.meta.requestStatus === "fulfilled" &&
                (res.payload as OdPLegacyList)?.page &&
                (res.payload as OdPLegacyList).page !== filtriOdP.page
            ) {
                dispatch(
                    setFiltriOdPSpallaSchedulazione({
                        ...filtriOdP,
                        page: (res.payload as OdPLegacyList).page,
                    })
                );
            }
        });
    }, [odpAttivoId]);

    // Reparti per cui caricare il dettaglio: union di reparti selezionati
    // e reparti degli operatori selezionati (servono per mostrare il dettaglio
    // operatore quando si filtra per operatore senza filtrare il reparto).
    // Se è selezionato il sentinel "Senza operatore", servono i dettagli di tutti
    // i reparti (o di quelli selezionati) per calcolare il carico generico.
    const repartiDettaglio = useMemo(() => {
        const ids = new Set<number>(filtri.repartiSelezionati);
        if (filtri.operatoriSelezionati.length > 0) {
            operatori.forEach((op) => {
                if (
                    op.id != null &&
                    op.reparto != null &&
                    filtri.operatoriSelezionati.includes(op.id)
                ) {
                    ids.add(op.reparto);
                }
            });
            if (filtri.operatoriSelezionati.includes(SENZA_OPERATORE_ID)) {
                const repartiTarget = filtri.repartiSelezionati.length > 0
                    ? reparti.filter((r) => r.id != null && filtri.repartiSelezionati.includes(r.id))
                    : reparti.filter((r) => r.attivo);
                repartiTarget.forEach((r) => {
                    if (r.id != null) ids.add(r.id);
                });
            }
        }
        return Array.from(ids);
    }, [filtri.repartiSelezionati, filtri.operatoriSelezionati, operatori, reparti]);

    // Fetch carico dettaglio per ogni reparto necessario
    useEffect(() => {
        if (!ganttDataDa) return;
        const dataDaISO = convertToDateTime(ganttDataDa)?.startOf("week").toFormat("yyyy-MM-dd");
        if (!dataDaISO) return;
        const giorni = numeroSettimane * 7;

        if (repartiDettaglio.length === 0) {
            dispatch(resetCaricoDettaglio());
            return;
        }
        repartiDettaglio.forEach((repartoId) => {
            dispatch(fetchCaricoReparto({ dataDa: dataDaISO, giorni, repartoId }));
        });
    }, [ganttDataDa, numeroSettimane, repartiDettaglio]);

    // ============================================
    // REFRESH CARICO REPARTI
    // ============================================

    const refreshCarico = () => {
        if (!ganttDataDa) return;
        const dataDaISO = convertToDateTime(ganttDataDa)?.startOf("week").toFormat("yyyy-MM-dd");
        if (!dataDaISO) return;
        const giorni = numeroSettimane * 7;
        dispatch(fetchCaricoReparti({ dataDa: dataDaISO, giorni }));
        repartiDettaglio.forEach((repartoId) => {
            dispatch(fetchCaricoReparto({ dataDa: dataDaISO, giorni, repartoId }));
        });
    };

    const refreshVariazioni = () => {
        if (!ganttDataDa) return;
        const dataDaISO = convertToDateTime(ganttDataDa)?.startOf("week").toFormat("yyyy-MM-dd");
        if (!dataDaISO) return;
        const dataA = convertToDateTime(dataDaISO)?.plus({ weeks: numeroSettimane }).toFormat("yyyy-MM-dd");
        if (!dataA) return;
        dispatch(fetchVariazioni({
            dataDa: dataDaISO,
            dataA,
            repartiIds: filtri.repartiSelezionati.length > 0 ? filtri.repartiSelezionati : undefined,
            includeOperatoriDiReparto: filtri.repartiSelezionati.length > 0,
        }));
    };

    // ============================================
    // HANDLERS - GANTT NAVIGAZIONE
    // ============================================

    const handlePeriodoChange = (event: SelectChangeEvent<number>) => {
        setNumeroSettimane(event.target.value as 2 | 4 | 6);
        dispatch(setFiltriVisualizzazioneGantt({...filtriVisualizzazioneGantt, numeroSettimane: event.target.value as 2 | 4 | 6}));
    };

    const handleSettimanaIndietro = () => {
        const nuovaDataDa = convertToDateTime(ganttDataDa)?.minus({weeks: 1}).toFormat("yyyy-MM-dd")||""
        setGanttDataDa(nuovaDataDa  );
        dispatch(setFiltriVisualizzazioneGantt({...filtriVisualizzazioneGantt, dataDa: nuovaDataDa}));

    };

    const handleSettimanaAvanti = () => {
        const nuovaDataDa = convertToDateTime(ganttDataDa)?.plus({weeks: 1}).toFormat("yyyy-MM-dd")||""
        setGanttDataDa(nuovaDataDa  );
        dispatch(setFiltriVisualizzazioneGantt({...filtriVisualizzazioneGantt, dataDa: nuovaDataDa}));

    };

    const handleOggi = () => {
        const nuovaDataDa = DateTime.now().startOf("week").toFormat("yyyy-MM-dd")
        setGanttDataDa(nuovaDataDa  );
        dispatch(setFiltriVisualizzazioneGantt({...filtriVisualizzazioneGantt, dataDa: nuovaDataDa}));

    };

    // ============================================
    // HANDLERS - GANTT D&D CALLBACKS
    // ============================================

    const handleOdPDrop = async (odpId: number, data: string) => {
        // TODO: chiamata backend per creare frazione
        // Il backend creerà la frazione con fasi in sequenza,
        // poi fetch frazioni per aggiornare il gantt
        console.log("=== DROP ODP ===", {odpId, data});

        const risposta = await dispatch(schedulaFrazione({odpId, data}))
        if (risposta.meta.requestStatus === "fulfilled") {
            setRefreshOdp(true)
            refreshCarico();
        }
    };

    const handleFaseMove = async (
        faseFrazioneId: number,
        nuovaDataDa: string,
        nuovaDataA: string
    ) => {
        const risposta = await dispatch(rischedulaFaseFrazione({
            faseFrazioneId,
            data_inizio_pianificata: nuovaDataDa,
            data_fine_pianificata: nuovaDataA,
        }));
        if (risposta.meta.requestStatus === "fulfilled") {
            setRefreshOdp(true);
            refreshCarico();
        }
    };

    const handleFaseResize = async (
        faseFrazioneId: number,
        nuovaDataDa: string | null,
        nuovaDataA: string | null
    ) => {
        const risposta = await dispatch(rischedulaFaseFrazione({
            faseFrazioneId,
            ...(nuovaDataDa && {data_inizio_pianificata: nuovaDataDa}),
            ...(nuovaDataA && {data_fine_pianificata: nuovaDataA}),
        }));
        if (risposta.meta.requestStatus === "fulfilled") {
            setRefreshOdp(true);
            refreshCarico();
        }
    };

    const handleFrazioneMove = async (
        frazioneId: number,
        nuovaDataInizio: string
    ) => {
        const risposta = await dispatch(rischedulaFrazione({
            frazioneId,
            nuovaDataInizio,
        }));
        if (risposta.meta.requestStatus === "fulfilled") {
            setRefreshOdp(true);
            refreshCarico();
        }
    };

    const handleFaseClick = (faseFrazioneId: number) => {
        const frazione = frazioniGantt.find((f) =>
            f.fasi?.some((fase) => fase.id === faseFrazioneId)
        );
        if (frazione?.odp !== undefined) {
            setOdpAttivoId(frazione.odp);
        }
    };


    const onFaseDelete = (
        faseFrazioneToDelete: FaseFrazione
    ) => {
        setFaseFrazioneToDelete(faseFrazioneToDelete)
        setOpenConferma(true)

    };

    const onFaseEdit = (faseFrazione: FaseFrazione) => {
        setFaseFrazioneToEdit(faseFrazione);
        setOpenEditFaseFrazione(true);
    };

    const onFrazioneDelete = (
        frazioneToDelete: FrazioneOdP
    ) => {
        setFrazioneToDelete(frazioneToDelete)
        setOpenConferma(true)

    };

    // ============================================
    // HANDLERS - VARIAZIONI DISPONIBILITÀ
    // ============================================

    const handleVariazioneCreate = (dataDrop: string) => {
        // Pre-imposta reparto se filtro su un solo reparto, altrimenti lascia che lo scelga il modal
        const repartoPreset = filtri.repartiSelezionati.length === 1
            ? filtri.repartiSelezionati[0]
            : undefined;
        setVariazioneToEdit({
            reparto: repartoPreset as number,
            operatore: null,
            data_da: dataDrop,
            data_a: dataDrop,
            ore_giornaliere: 1,
            note: "",
        });
        setOpenVariazioneModal(true);
    };

    const handleVariazioneClick = (v: VariazioneDisponibilita) => {
        setVariazioneToEdit(v);
        setOpenVariazioneModal(true);
    };

    const handleVariazioneMove = async (
        variazioneId: number,
        nuovaDataDa: string,
        nuovaDataA: string,
    ) => {
        const result = await dispatch(updateVariazionePartial({
            id: variazioneId,
            patch: { data_da: nuovaDataDa, data_a: nuovaDataA },
        }));
        if (updateVariazionePartial.fulfilled.match(result)) {
            refreshCarico();
        } else {
            toast.error("Impossibile spostare il placeholder: " + (result.error?.message || "errore"));
        }
    };

    const handleVariazioneResize = async (
        variazioneId: number,
        nuovaDataDa: string | null,
        nuovaDataA: string | null,
    ) => {
        const patch: Partial<VariazioneDisponibilita> = {};
        if (nuovaDataDa) patch.data_da = nuovaDataDa;
        if (nuovaDataA) patch.data_a = nuovaDataA;
        const result = await dispatch(updateVariazionePartial({ id: variazioneId, patch }));
        if (updateVariazionePartial.fulfilled.match(result)) {
            refreshCarico();
        } else {
            toast.error("Impossibile ridimensionare il placeholder: " + (result.error?.message || "errore"));
        }
    };

    // ============================================
    // HANDLERS
    // ============================================

    const handlePaginationChange = (
        event: React.ChangeEvent<unknown>,
        page: number
    ) => {
        const nuoviFiltri = {...filtriOdP, page};
        dispatch(setFiltriOdPSpallaSchedulazione(nuoviFiltri));
        dispatch(fetchOdPLegacy(nuoviFiltri));
    };

    const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
        const numero_record = event.target.value as number;
        const nuoviFiltri: FiltriOdPLegacy = {
            ...filtriOdP,
            numero_record,
            page: 1,
        };
        dispatch(setFiltriOdPSpallaSchedulazione(nuoviFiltri));
        dispatch(fetchOdPLegacy(nuoviFiltri));
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <DndProvider backend={HTML5Backend}>
            <Box sx={{p: 2, height: "calc(100vh - 64px)", overflow: "hidden"}}>
                <Grid container spacing={1} sx={{height: "100%"}}>
                    {/* ============================== */}
                    {/* AREA PRINCIPALE - GANTT */}
                    {/* ============================== */}
                    <Grid
                        size={{xs: 12, lg: sidebarAperta ? 9 : 12}}
                        sx={{
                            transition: "all 0.3s ease",
                            height: "100%",
                            overflow: "hidden",
                        }}
                    >
                        <Card sx={{height: "100%", display: "flex", flexDirection: "column"}}>
                            {/* Header Gantt con navigazione */}
                            <CardContent sx={{py: 1}}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold">
                                        Schedulazione
                                    </Typography>

                                    {/* Navigazione temporale */}
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        sx={{alignItems: "center"}}
                                    >
                                        <IconButton
                                            onClick={handleSettimanaIndietro}
                                            size="small"
                                        >
                                            <NavigateBeforeIcon/>
                                        </IconButton>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<TodayIcon/>}
                                            onClick={handleOggi}
                                        >
                                            Oggi
                                        </Button>
                                        <IconButton
                                            onClick={handleSettimanaAvanti}
                                            size="small"
                                        >
                                            <NavigateNextIcon/>
                                        </IconButton>

                                        <Typography
                                            variant="body2"
                                            sx={{mx: 1, minWidth: 140, textAlign: "center"}}
                                        >
                                            {convertToDateTime(ganttDataDa)?.toFormat("dd MMM yyyy", {locale: "it"})}
                                        </Typography>

                                        {/* Selettore periodo */}
                                        <FormControl size="small" sx={{minWidth: 130}}>
                                            <Select
                                                value={numeroSettimane}
                                                onChange={handlePeriodoChange}
                                            >
                                                {OPZIONI_PERIODO.map((opzione) => (
                                                    <MenuItem
                                                        key={opzione.value}
                                                        value={opzione.value}
                                                    >
                                                        {opzione.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>

                                    {/* Toggle sidebar */}
                                    {!sidebarAperta && (
                                        <IconButton
                                            onClick={() => setSidebarAperta(true)}
                                            size="small"
                                            color="primary"
                                        >
                                            <ChevronLeftIcon/>
                                        </IconButton>
                                    )}
                                </Stack>
                            </CardContent>
                            <Divider/>

                            {/* Gantt Timeline */}
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflow: "auto",
                                }}
                            >
                                <SchedulazioneGanttTimeline
                                    dataDa={convertToDateTime(ganttDataDa)!}
                                    numeroSettimane={numeroSettimane}
                                    onOdPDrop={handleOdPDrop}
                                    onFaseMove={handleFaseMove}
                                    onFaseResize={handleFaseResize}
                                    onFrazioneMove={handleFrazioneMove}
                                    onFaseClick={handleFaseClick}
                                    onFrazioneDelete={onFrazioneDelete}
                                    onFaseDelete={onFaseDelete}
                                    onFaseEdit={onFaseEdit}
                                    odpAttivoId={odpAttivoId}
                                    onOdPSelect={setOdpAttivoId}
                                    repartiSelezionati={filtriOdP?.reparto}
                                    operatoriSelezionati={filtri.operatoriSelezionati}
                                    onVariazioneCreate={handleVariazioneCreate}
                                    onVariazioneMove={handleVariazioneMove}
                                    onVariazioneResize={handleVariazioneResize}
                                    onVariazioneClick={handleVariazioneClick}
                                />
                            </Box>
                        </Card>
                    </Grid>

                    {/* ============================== */}
                    {/* SIDEBAR DESTRA - OdP Legacy */}
                    {/* ============================== */}
                    {sidebarAperta && (
                        <Grid
                            size={{xs: 12, lg: 3}}
                            sx={{
                                height: "100%",
                                overflow: "hidden",
                                transition: "all 0.3s ease",
                            }}
                        >
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {/* Header sidebar */}
                                <CardContent sx={{py: 1}}>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            OdP da schedulare
                                        </Typography>
                                        <IconButton
                                            onClick={() => setSidebarAperta(false)}
                                            size="small"
                                        >
                                            <ChevronRightIcon/>
                                        </IconButton>
                                    </Stack>

                                    {/* Ricerca OdP */}
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{mt: 1}}
                                    >
                                        <InputLabel>Ricerca OdP</InputLabel>
                                        <OutlinedInput
                                            type="text"
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={filtri.applicaConSearch}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        <SearchIcon fontSize="small"/>
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label="Ricerca OdP"
                                            placeholder="Cliente, articolo..."
                                            value={filtri.searchValue}
                                            onChange={(e) => filtri.setSearchValue(e.target.value)}
                                            onBlur={filtri.applicaConSearch}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    filtri.applicaConSearch();
                                                }
                                            }}
                                        />
                                    </FormControl>

                                    {/* Toggle filtriOdP */}
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        sx={{
                                            mt: 1,
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => setFiltriVisibili(!filtriVisibili)}
                                            color={filtriVisibili ? "primary" : "default"}
                                        >
                                            <TuneIcon fontSize="small"/>
                                        </IconButton>

                                        {/* Chips filtriOdP attivi */}
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{flexGrow: 1, flexWrap: "wrap", gap: 0.5}}
                                        >
                                            {filtri.statiSelezionati.length > 0 && (
                                                <Chip
                                                    label={`Stato (${filtri.statiSelezionati.length})`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{height: 20, fontSize: "0.65rem"}}
                                                    onDelete={() => filtri.setStatiSelezionati([])}
                                                />
                                            )}
                                            {filtri.repartiSelezionati.length > 0 && (
                                                <Chip
                                                    label={`Reparti (${filtri.repartiSelezionati.length})`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{height: 20, fontSize: "0.65rem"}}
                                                    onDelete={() => filtri.setRepartiSelezionati([])}
                                                />
                                            )}
                                            {filtri.operatoriSelezionati.length > 0 && (
                                                <Chip
                                                    label={`Operatori (${filtri.operatoriSelezionati.length})`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{height: 20, fontSize: "0.65rem"}}
                                                    onDelete={() => filtri.setOperatoriSelezionati([])}
                                                />
                                            )}
                                            {(filtri.dataDa || filtri.dataA) && (
                                                <Chip
                                                    label="Date"
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{height: 20, fontSize: "0.65rem"}}
                                                    onDelete={() => {
                                                        filtri.setDataDa("");
                                                        filtri.setDataA("");
                                                    }}
                                                />
                                            )}
                                            {filtri.nonPerfettamenteSchedulati && (
                                                <Chip
                                                    label="Non perf. schedulati"
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{height: 20, fontSize: "0.65rem"}}
                                                    onDelete={() => filtri.setNonPerfettamenteSchedulati(false)}
                                                />
                                            )}
                                        </Stack>

                                        {(filtri.statiSelezionati.length > 0 ||
                                            filtri.repartiSelezionati.length > 0 ||
                                            filtri.operatoriSelezionati.length > 0 ||
                                            filtri.dataDa || filtri.dataA ||
                                            filtri.nonPerfettamenteSchedulati) && (
                                            <IconButton
                                                size="small"
                                                onClick={filtri.resetFiltri}
                                                color="primary"
                                            >
                                                <FilterAltOffIcon fontSize="small"/>
                                            </IconButton>
                                        )}
                                    </Stack>
                                </CardContent>

                                {/* Pannello filtriOdP espandibile */}
                                <Collapse in={filtriVisibili} timeout="auto" sx={{ flexShrink: 0 }}>
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            pb: 1,
                                            backgroundColor: theme.palette.grey[50],
                                        }}
                                    >
                                        {/* Filtro Stato */}
                                        <Typography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{display: "block", mb: 0.5}}
                                        >
                                            Stato OdP
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{flexWrap: "wrap", gap: 0.5, mb: 1}}
                                        >
                                            {Object.entries(STATI_ODP).map(
                                                ([key, value]) => {
                                                    const keyNum = Number(key);
                                                    const isSelected =
                                                        filtri.statiSelezionati.includes(keyNum);
                                                    return (
                                                        <Chip
                                                            key={key}
                                                            label={value}
                                                            size="small"
                                                            color={isSelected ? "primary" : "default"}
                                                            variant={isSelected ? "filled" : "outlined"}
                                                            onClick={() => filtri.toggleStato(keyNum)}
                                                            sx={{
                                                                height: 22,
                                                                fontSize: "0.7rem",
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    );
                                                }
                                            )}
                                        </Stack>

                                        {/* Filtro Clienti */}

                                        <AutocompleteClientiAsync
                                            label="Filtra per cliente"
                                            value={filtri.clienteSelezionato}
                                            onChange={(newValue) => filtri.setClienteSelezionato(newValue)}
                                            disabled={false}
                                            minSearchLength={2}
                                            debounceDelay={300}
                                        />
                                        {/* Filtro Reparti */}
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            value={reparti.filter((r) =>
                                                filtri.repartiSelezionati.includes(r.id || 0)
                                            )}
                                            options={reparti.filter((r) => r.attivo)}
                                            getOptionLabel={(option) =>
                                                option.descrizione
                                                    ? `${option.codice} - ${option.descrizione}`
                                                    : option.codice || ""
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                option.id === value.id
                                            }
                                            onChange={(_, newValue) => {
                                                filtri.setRepartiSelezionati(
                                                    newValue.map((r) => r.id || 0)
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Reparti"
                                                    sx={{
                                                        backgroundColor:
                                                            filtri.repartiSelezionati.length > 0
                                                                ? theme.palette.action.selected
                                                                : "inherit",
                                                    }}
                                                />
                                            )}
                                            sx={{mb: 1}}
                                        />

                                        {/* Filtro Operatori */}
                                        {(() => {
                                            const opzioneSenzaOperatore: Operatore = {
                                                id: SENZA_OPERATORE_ID,
                                                codice: "—",
                                                nominativo: "(Senza operatore)",
                                                attivo: true,
                                            };
                                            const opzioniOperatori: Operatore[] = [
                                                opzioneSenzaOperatore,
                                                ...operatori.filter((op) => op.attivo),
                                            ];
                                            return (
                                                <Autocomplete
                                                    multiple
                                                    size="small"
                                                    value={opzioniOperatori.filter((op) =>
                                                        filtri.operatoriSelezionati.includes(op.id || 0)
                                                    )}
                                                    options={opzioniOperatori}
                                                    getOptionLabel={(option) =>
                                                        option.id === SENZA_OPERATORE_ID
                                                            ? option.nominativo || ""
                                                            : option.nominativo
                                                                ? `${option.codice} - ${option.nominativo}`
                                                                : option.codice || ""
                                                    }
                                                    isOptionEqualToValue={(option, value) =>
                                                        option.id === value.id
                                                    }
                                                    onChange={(_, newValue) => {
                                                        filtri.setOperatoriSelezionati(
                                                            newValue.map((op) => op.id || 0)
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Operatori"
                                                            sx={{
                                                                backgroundColor:
                                                                    filtri.operatoriSelezionati.length > 0
                                                                        ? theme.palette.action.selected
                                                                        : "inherit",
                                                            }}
                                                        />
                                                    )}
                                                    sx={{mb: 1}}
                                                />
                                            );
                                        })()}

                                        {/* Filtro Date */}
                                        <Stack direction="row" spacing={1} sx={{mb: 1}}>
                                            <TextField
                                                size="small"
                                                type="date"
                                                label="Data da"
                                                value={filtri.dataDa}
                                                onChange={(e) => filtri.setDataDa(e.target.value)}
                                                fullWidth
                                                slotProps={{
                                                    inputLabel: {shrink: true},
                                                }}
                                                sx={{
                                                    backgroundColor: filtri.dataDa
                                                        ? theme.palette.action.selected
                                                        : "inherit",
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                type="date"
                                                label="Data a"
                                                value={filtri.dataA}
                                                onChange={(e) => filtri.setDataA(e.target.value)}
                                                fullWidth
                                                slotProps={{
                                                    inputLabel: {shrink: true},
                                                }}
                                                sx={{
                                                    backgroundColor: filtri.dataA
                                                        ? theme.palette.action.selected
                                                        : "inherit",
                                                }}
                                            />
                                        </Stack>

                                        {/* Filtro Non perfettamente schedulati */}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={filtri.nonPerfettamenteSchedulati}
                                                    onChange={(e) => filtri.setNonPerfettamenteSchedulati(e.target.checked)}
                                                />
                                            }
                                            label="Non perfettamente schedulati"
                                            slotProps={{typography: {variant: "caption"}}}
                                            sx={{mb: 1}}
                                        />
                                    </Box>
                                </Collapse>
                                <Divider/>

                                {/* Lista OdP scrollabile */}
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        flexShrink: 1,
                                        minHeight: 0,
                                        overflow: "auto",
                                        px: 1,
                                        py: 0.5,
                                    }}
                                >
                                    {/* Card per creare nuovo placeholder di disponibilità */}
                                    <VariazioneSidebarCard />

                                    {odpLegacyList.errorsStack.status ===
                                        ErrorStatusTypes.PENDING && (
                                            <Box sx={{textAlign: "center", py: 4}}>
                                                <CircularProgress size={24}/>
                                            </Box>
                                        )}

                                    {odpLegacyList.results.map((odp: OdPLegacy) => (
                                        <OdPSidebarCard
                                            key={odp.id}
                                            odp={odp}
                                            isAttivo={odpAttivoId === odp.id}
                                            onSelect={setOdpAttivoId}
                                            onNavigaAFrazione={(dataInizio) => {
                                                const dt = DateTime.fromISO(dataInizio);
                                                if (!dt.isValid) return;
                                                const nuovaDataDa = dt.startOf("week").toFormat("yyyy-MM-dd");
                                                setGanttDataDa(nuovaDataDa);
                                                dispatch(setFiltriVisualizzazioneGantt({...filtriVisualizzazioneGantt, dataDa: nuovaDataDa}));
                                                setOdpAttivoId(odp.id);
                                            }}
                                            onOstacoloChange={() => setReset(true)}
                                        />
                                    ))}

                                    {/* Empty state */}
                                    {odpLegacyList.results.length === 0 &&
                                        odpLegacyList.errorsStack.status !==
                                        ErrorStatusTypes.PENDING && (
                                            <Box sx={{textAlign: "center", py: 4}}>
                                                <FactoryIcon
                                                    sx={{
                                                        fontSize: 48,
                                                        color: "grey.400",
                                                        mb: 1,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Nessun OdP trovato
                                                </Typography>
                                            </Box>
                                        )}
                                </Box>

                                {/* Paginazione sidebar */}
                                <Divider/>
                                <Box sx={{py: 1, px: 1}}>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <FormControl size="small" sx={{minWidth: 60}}>
                                            <Select
                                                value={filtriOdP.numero_record || 10}
                                                onChange={handleRecordsPerPageChange}
                                                variant="standard"
                                                sx={{fontSize: "0.75rem"}}
                                            >
                                                <MenuItem value={10}>10</MenuItem>
                                                <MenuItem value={20}>20</MenuItem>
                                                <MenuItem value={50}>50</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Pagination
                                            count={odpLegacyList.num_pages}
                                            page={filtriOdP.page || 1}
                                            onChange={handlePaginationChange}
                                            color="primary"
                                            size="small"
                                            siblingCount={0}
                                        />
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{textAlign: "center", display: "block"}}
                                    >
                                        {odpLegacyList.count} OdP
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                <ConfermaAnnulla
                    handleAnnulla={handleConfermaClose}
                    handleConferma={async () => {
                        if (faseFrazioneToDelete) {
                             await dispatch(deleteFaseFrazione({frazioneId: faseFrazioneToDelete.frazione!, faseFrazioneToDelete}))
                        }
                        if (frazioneToDelete) {
                            await dispatch(deleteFrazioneOdP({frazioneOdPToDelete: frazioneToDelete}))
                        }
                        handleConfermaClose();
                        setRefreshOdp(true)
                        refreshCarico();
                    }}
                    domanda={
                        faseFrazioneToDelete ?
                        `Sicuro di voler cancellare la schedulazione di ${faseFrazioneToDelete.descrizione} di questa fase`:
                        `Sicuro di voler cancellare la frazione schedulata di ${frazioneToDelete?.odp_display}`
                    }
                    open={openConferma}
                />

                <FaseFrazioneEditModal
                    open={openEditFaseFrazione}
                    faseFrazione={faseFrazioneToEdit}
                    onClose={() => {
                        setOpenEditFaseFrazione(false);
                        setFaseFrazioneToEdit(undefined);
                    }}
                    onSaveSuccess={() => {
                        setOpenEditFaseFrazione(false);
                        setFaseFrazioneToEdit(undefined);
                        setRefreshOdp(true);
                        refreshCarico();
                    }}
                />

                <VariazioneEditModal
                    open={openVariazioneModal}
                    variazione={variazioneToEdit}
                    onClose={() => {
                        setOpenVariazioneModal(false);
                        setVariazioneToEdit(undefined);
                    }}
                    onSaved={() => {
                        setOpenVariazioneModal(false);
                        setVariazioneToEdit(undefined);
                        refreshCarico();
                        refreshVariazioni();
                    }}
                />
            </Box>
        </DndProvider>
    );
};

export default SchedulazionePage;